import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import debounce from 'lodash/debounce';
import find from 'lodash/find';
import { gettext } from '@eb/i18n';

import { Icon } from '@eb/eds-icon';
import { IconButton } from '@eb/eds-icon-button';
import { CrossChunky as CrossChunkySvg } from '@eb/eds-iconography';
import { MagnifyingGlassChunky as MagnifyingGlassChunkySvg } from '@eb/eds-iconography';
import { HAS_WINDOW } from '@eb/feature-detection';
import { BORDER_NONE } from '@eb/eds-input-field';

import { openEventUrl } from '../../utils/http';
import {
    GA_HOME_FEED_CATEGORY,
    GA_VIEW_EVENT_AUTOCOMPLETE,
} from '../../constants/analytics';
import { SEARCH_QUERY_MAX_LENGTH } from '../../constants/constants';
import { EVENT_TYPE, SUGGESTIONS_PROPTYPE } from '../../constants';

import AutocompleteField from './AutocompleteFieldWithFallback';
import { parseHistorySuggestions, storeQuery } from '@eb/redux-destination';

const EVENT_DEBOUNCE_INTERVAL = 200;

const placeHolderText = gettext('Search events');

//TODO: Align this autocomplete with the one used on search pages
export default class SearchAutocomplete extends PureComponent {
    static propTypes = {
        borderType: PropTypes.string,
        hasHistory: PropTypes.bool,
        handleApplyFilter: PropTypes.func,
        locationSlug: PropTypes.string,
        suggestions: SUGGESTIONS_PROPTYPE,
        searchQuery: PropTypes.string,
        shouldFocusInput: PropTypes.bool,
        onSearchSuggestionChange: PropTypes.func,
        onSearchFilterChange: PropTypes.func,
        onSearchQueryChange: PropTypes.func,
        onClearButtonClick: PropTypes.func,
        onFocus: PropTypes.func,
        onBlur: PropTypes.func,
        trackGAEvent: PropTypes.func,
        parentId: PropTypes.string,
    };

    static defaultProps = {
        borderType: BORDER_NONE,
        parentId: '',
    };

    _handleSearchAutocompleteSelect = (suggestions, selected = '') => {
        const {
            onSearchFilterChange,
            handleApplyFilter,
            locationSlug,
        } = this.props;

        //User selected an event from the dropdown
        if (selected.indexOf(`${EVENT_TYPE}/`) > -1) {
            const eid = selected.replace(`${EVENT_TYPE}/`, '');

            return this._handleEventSelect(eid, suggestions);
        }

        //User selected a query from the suggested options
        //So we should set the state of the component and run the expected search
        onSearchFilterChange({ q: selected, locationSlug });

        const actionType = (find(suggestions, { value: selected }) || {})
            .suggestionType;

        return handleApplyFilter({ query: selected, locationSlug, actionType });
    };

    _handleSearchAutocompleteChange = (searchQuery) => {
        const { onSearchQueryChange } = this.props;

        onSearchQueryChange(searchQuery);

        this._requestSearchSuggestions(searchQuery);
    };

    _requestSearchSuggestions = debounce((searchQuery) => {
        const { onSearchSuggestionChange } = this.props;

        onSearchSuggestionChange(searchQuery);
    }, EVENT_DEBOUNCE_INTERVAL);

    _handleEventSelect = (eventId, suggestions) => {
        const { hasHistory, searchQuery, trackGAEvent } = this.props;
        const suggestedEvent = find(suggestions, { value: `event/${eventId}` });

        if (!isEmpty(suggestedEvent)) {
            const { ticketsBy, url } = suggestedEvent;

            trackGAEvent({
                category: GA_HOME_FEED_CATEGORY,
                action: GA_VIEW_EVENT_AUTOCOMPLETE,
                label: eventId,
                onWindowUnload: true,
            });

            if (hasHistory && HAS_WINDOW) {
                storeQuery(searchQuery);
            }

            openEventUrl(url, ticketsBy);
        }
    };

    _handleKeyDown = ({ event }) => {
        const { hasHistory, searchQuery } = this.props;
        const isEnterKey = event.charCode === 13 || event.keyCode === 13;

        if (hasHistory && HAS_WINDOW) {
            if (isEnterKey) {
                storeQuery(searchQuery);
            }
        }
    };

    render() {
        const {
            borderType,
            suggestions: { query, event, events },
            searchQuery,
            onClearButtonClick,
            onFocus,
            onBlur,
            shouldFocusInput,
            parentId,
            suffix,
            hasMinAsideSpacing,
        } = this.props;
        const historySuggestions = parseHistorySuggestions({
            inputValue: searchQuery,
        });

        let clearSearchButton = undefined;

        let suggestions = [...historySuggestions, ...events];

        if (!isEmpty(searchQuery)) {
            suggestions = [...historySuggestions, ...query, ...event];

            clearSearchButton = (
                <span className="eds-show-down-sw">
                    <IconButton
                        iconType={<CrossChunkySvg />}
                        title={gettext('Clear search')}
                        onClick={onClearButtonClick}
                    />
                </span>
            );
        }

        const autocompleteIcon = (
            <Icon size="small" type={<MagnifyingGlassChunkySvg />} />
        );

        return (
            <div className="search-input__query" data-spec="event-autocomplete">
                <AutocompleteField
                    id={`eventPicker${parentId}`}
                    name={`eventPicker${parentId}`}
                    type="search"
                    suggestions={suggestions}
                    value={searchQuery}
                    suffix={suffix || clearSearchButton}
                    prefix={autocompleteIcon}
                    onChange={this._handleSearchAutocompleteChange}
                    onSelect={this._handleSearchAutocompleteSelect.bind(
                        null,
                        suggestions,
                    )}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    onKeyDown={this._handleKeyDown}
                    shouldFocusInput={shouldFocusInput}
                    hideOutline={true}
                    isV2={true}
                    borderType={borderType}
                    placeholder={placeHolderText}
                    preventInputValuePrefill={true}
                    shouldRemoveConflictiveAria={true}
                    maxLength={SEARCH_QUERY_MAX_LENGTH}
                    hasMinAsideSpacing={hasMinAsideSpacing}
                />
            </div>
        );
    }
}
